import { normalizeSitePath } from './analyticsEngagement';

const HOMEPAGE_STORAGE_KEY = 'vwoHomepageCampaigns';
const PAGE_STORAGE_KEY = 'vwoPageCampaigns';

const getNormalizedPath = (pathname) => {
  if (typeof window === 'undefined' && !pathname) {
    return '/';
  }

  return normalizeSitePath(
    pathname || window.location.pathname,
    (typeof window !== 'undefined' && window.__PATH_PREFIX__) || '',
  );
};

const stripTrailingSlash = (path) => path.replace(/\/+$/, '') || '/';

export const isHomepagePath = (pathname) =>
  stripTrailingSlash(getNormalizedPath(pathname)) === '/';

export const isDemoOrPricingPath = (pathname) => {
  const path = stripTrailingSlash(getNormalizedPath(pathname));
  return path === '/get-free-demo' || path === '/pricing';
};

const readJson = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* storage blocked — attribution is best-effort */
  }
};

const toCampaignRecord = (campaignId, meta = {}) => ({
  campaignId: String(campaignId),
  campaignName: meta.campaignName ?? null,
  variationId:
    meta.variationId != null && meta.variationId !== ''
      ? String(meta.variationId)
      : null,
  variationName: meta.variationName ?? null,
});

const campaignSignature = (record) =>
  [
    record.campaignId,
    record.variationId ?? '',
    record.campaignName ?? '',
    record.variationName ?? '',
  ].join('|');

const getLiveCampaignRecords = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const campaigns = window.abTesting?.campaigns || {};
  return Object.keys(campaigns)
    .sort()
    .map((id) => toCampaignRecord(id, campaigns[id]))
    .filter((record) => record.campaignId);
};

const normalizeCampaignList = (stored) => {
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter((item) => item?.campaignId);
};

export const getPersistedHomepageCampaigns = () =>
  normalizeCampaignList(readJson(HOMEPAGE_STORAGE_KEY));

const getPageCampaignMap = () => {
  const stored = readJson(PAGE_STORAGE_KEY);
  return stored && typeof stored === 'object' ? stored : {};
};

export const getPersistedPageCampaigns = (pathname) => {
  const path = stripTrailingSlash(getNormalizedPath(pathname));
  return normalizeCampaignList(getPageCampaignMap()[path]);
};

const mergeCampaignLists = (existing, incoming) => {
  const byId = new Map(existing.map((item) => [item.campaignId, item]));
  incoming.forEach((item) => {
    byId.set(item.campaignId, item);
  });
  return Array.from(byId.values());
};

/**
 * Only campaigns that newly appeared / changed since the last sync.
 * Avoids copying leftover demo ids into the pricing bucket (and vice versa)
 * just because they are still sitting in window.abTesting.
 */
const getNewlyAppliedCampaigns = () => {
  const live = getLiveCampaignRecords();
  const previous = window.__vwoAttributionLastSeen || {};
  const nextSeen = {};
  const newlyApplied = [];

  live.forEach((record) => {
    const signature = campaignSignature(record);
    nextSeen[record.campaignId] = signature;
    if (previous[record.campaignId] !== signature) {
      newlyApplied.push(record);
    }
  });

  window.__vwoAttributionLastSeen = nextSeen;
  return newlyApplied;
};

/**
 * Persist VWO campaigns by the page where they were applied.
 * - Homepage (`/`): remembered for flowthrough (even if seen after a form page)
 * - Demo / pricing: remembered per path only (never crosses to the other form)
 * No env allowlist — whatever VWO newly applies on that page is kept.
 */
export const syncCampaignsFromAbTesting = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const newlyApplied = getNewlyAppliedCampaigns();
  if (newlyApplied.length === 0) {
    return;
  }

  if (isHomepagePath()) {
    writeJson(
      HOMEPAGE_STORAGE_KEY,
      mergeCampaignLists(getPersistedHomepageCampaigns(), newlyApplied),
    );
    return;
  }

  if (!isDemoOrPricingPath()) {
    return;
  }

  const path = stripTrailingSlash(getNormalizedPath());
  const pageMap = getPageCampaignMap();
  pageMap[path] = mergeCampaignLists(
    normalizeCampaignList(pageMap[path]),
    newlyApplied,
  );
  writeJson(PAGE_STORAGE_KEY, pageMap);
};

/** @deprecated use syncCampaignsFromAbTesting */
export const syncHomepageCampaignsFromAbTesting = syncCampaignsFromAbTesting;

const toReportingFields = (experiments) => {
  if (!experiments.length) {
    return {};
  }

  return {
    ab_campaign_id__vwo: experiments.map((e) => e.campaignId).join(','),
    ab_campaign_name__vwo: experiments
      .map((e) => e.campaignName ?? '')
      .join(','),
    ab_test_id__vwo: experiments.map((e) => e.variationId ?? '').join(','),
    ab_test_variant_name__vwo: experiments
      .map((e) => e.variationName ?? '')
      .join(','),
  };
};

/**
 * HubSpot / Chili Piper VWO fields from live VWO data (no hardcoded campaign IDs).
 *
 * Order: homepage if ever seen this session, then campaigns applied on the
 * submit page (demo or pricing). Demo never carries to pricing and vice versa.
 *
 * Examples:
 * - Homepage → Pricing → submit → `56,57`
 * - Pricing → Homepage → Pricing → submit → `56,57`
 * - Direct to Pricing → `57`
 * - Demo → Pricing → submit → `57` only
 */
export const buildAbReportingFields = () => {
  const experiments = [];
  const seen = new Set();

  const append = (items) => {
    items.forEach((item) => {
      if (!seen.has(item.campaignId)) {
        seen.add(item.campaignId);
        experiments.push(item);
      }
    });
  };

  append(getPersistedHomepageCampaigns());

  if (isDemoOrPricingPath()) {
    append(getPersistedPageCampaigns());
  }

  return toReportingFields(experiments);
};

export const vwoAttributionSetup = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const sync = () => {
    syncCampaignsFromAbTesting();
  };

  sync();

  if (window.__vwoAttributionSetupDone) {
    return;
  }
  window.__vwoAttributionSetupDone = true;
  window.addEventListener('abFlagChanged', sync);
};
